'use client';

import {useMemo} from 'react';
import {useAuth} from 'react-oidc-context';

/**
 * Hook to get authentication token for API requests
 *
 * This is the RECOMMENDED pattern for new features.
 * Provides direct access to OIDC token without manual localStorage access.
 *
 * @example
 * ```typescript
 * function MyComponent() {
 *   const token = useApiToken();
 *   const data = await documentsApi.getDocuments({ token });
 * }
 * ```
 *
 * @returns ID token for API authentication, or undefined if not authenticated
 */
export function useApiToken(): string | undefined {
  const auth = useAuth();
  return auth.user?.id_token;
}

/**
 * Hook to check if API token is available
 *
 * Useful for conditional rendering or early returns.
 *
 * @example
 * ```typescript
 * function MyComponent() {
 *   const hasToken = useHasApiToken();
 *   if (!hasToken) return <LoginPrompt />;
 *   // ... fetch data
 * }
 * ```
 */
export function useHasApiToken(): boolean {
  const token = useApiToken();
  return Boolean(token);
}

/**
 * Hook to get API headers with authentication
 *
 * Returns headers object ready to pass to fetch or API client.
 *
 * @example
 * ```typescript
 * function MyComponent() {
 *   const headers = useApiHeaders();
 *   const response = await fetch('/api/data', { headers });
 * }
 * ```
 */
export function useApiHeaders(): Record<string, string> {
  const token = useApiToken();

  return useMemo(() => {
    const headers: Record<string, string> = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }, [token]);
}
